import { useEffect, useState } from 'react';
import { Modal } from '@/components/primitives/Modal';
import { Button } from '@/components/primitives/Button';
import { startRenderJob } from '@/services/renders';
import type { RenderOutputResolution } from '@/types/api';

const RESOLUTION_OPTIONS: { value: RenderOutputResolution; label: string }[] = [
  { value: 'HD', label: 'HD (1280×720)' },
  { value: 'FHD', label: 'FHD (1920×1080)' },
  { value: 'UHD', label: 'UHD (3840×2160)' },
];

export interface StartRenderModalProps {
  isOpen: boolean;
  onClose: () => void;
  creativeId: number;
  creativeName: string;
  onStarted?: () => void;
}

export default function StartRenderModal({
  isOpen,
  onClose,
  creativeId,
  creativeName,
  onStarted,
}: StartRenderModalProps) {
  const [resolution, setResolution] = useState<RenderOutputResolution>('HD');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setResolution('HD');
    setError('');
    setSubmitting(false);
  }, [isOpen]);

  async function handleStart() {
    setError('');
    setSubmitting(true);
    try {
      await startRenderJob(creativeId, resolution);
      onStarted?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start render');
    } finally {
      setSubmitting(false);
    }
  }

  const footerBase = (
    <div className="aspectly:flex aspectly:justify-end aspectly:gap-3 aspectly:w-full">
      <Button variant="ghost" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={() => void handleStart()}
        disabled={submitting}
        className="aspectly:min-w-[120px]"
      >
        {submitting ? 'Starting…' : 'Render job'}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Export Video"
      subtitle={`Start a render job for ${creativeName}`}
      width="max-w-md"
      footer={footerBase}
    >
      <div className="aspectly:flex aspectly:flex-col">
        <p className="aspectly:text-sm aspectly:text-on-surface-variant aspectly:mb-6">
          The current saved creative (or latest on the server after you save) is snapshotted when
          the job starts.
        </p>

        <p className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:mb-3 aspectly:uppercase aspectly:tracking-wider aspectly:font-bold">
          Output resolution
        </p>

        <div className="aspectly:flex aspectly:flex-col aspectly:gap-3">
          {RESOLUTION_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`aspectly:flex aspectly:items-center aspectly:gap-3 aspectly:px-4 aspectly:py-3 aspectly:rounded-xl aspectly:border aspectly:cursor-pointer aspectly:transition-all ${
                resolution === opt.value
                  ? 'aspectly:border-primary aspectly:bg-primary-container/50 aspectly:text-on-surface' : 'aspectly:border-outline-variant/20 aspectly:text-on-surface-variant aspectly:hover:border-primary/30 aspectly:hover:bg-surface-container-low'
              }`}
            >
              <input
                type="radio"
                name="output-resolution"
                value={opt.value}
                checked={resolution === opt.value}
                onChange={() => setResolution(opt.value)}
                className="aspectly:accent-primary aspectly:w-4 aspectly:h-4 aspectly:cursor-pointer"
              />
              <span className="aspectly:text-sm aspectly:font-bold aspectly:tracking-wide">{opt.label}</span>
            </label>
          ))}
        </div>

        {error ? (
          <div className="aspectly:mt-4 aspectly:p-3 aspectly:rounded-lg aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm aspectly:font-medium">
            {error}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
